import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Button, Input, CustomSelect } from '../components';
import Background from '../components/Background';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AsyncSelect from 'react-select/async';
import { CityData } from '../data/cities';
import { searchCities } from '../services/geonames';
import { COMMON_TIMEZONES } from '../data/timezones';

// Custom styles for datepicker to match design system
import './Onboarding.css';

// Add proper imports at the top after existing imports
import useKeyboardNavigation from '../hooks/useKeyboardNavigation';
import { retryWithBackoff } from '../utils/retry';
import { getErrorMessage } from '../utils/errorHandler';
import logger from '../utils/logger';

interface OnboardingFormData {
  // Step 1
  name: string;
  gender?: string;

  // Step 2
  birth_date: Date | null;
  birth_hour: string;
  birth_minute: string;
  birth_ampm: string;
  birth_city: CityData | null;
  birth_timezone?: string; // Optional - calculated from coordinates, but user can provide if calculation fails

  // Step 3
  astrology_style: string;
}

const ONBOARDING_STORAGE_KEY = 'ai-astrologer-onboarding-progress';
const STORAGE_EXPIRY_HOURS = 24;

const saveProgress = (formData: OnboardingFormData, step: number) => {
  const progress = {
    formData,
    step,
    timestamp: Date.now()
  };
  localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(progress));
  logger.debug('Onboarding', 'Progress saved to localStorage', { step, hasName: !!formData.name, hasBirthDate: !!formData.birth_date, hasCity: !!formData.birth_city });
};

const loadProgress = (): { formData: Partial<OnboardingFormData>, step: number } | null => {
  try {
    const saved = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!saved) {
      logger.debug('Onboarding', 'No saved progress found in localStorage');
      return null;
    }

    const parsed = JSON.parse(saved);
    const hoursSinceSave = (Date.now() - parsed.timestamp) / (1000 * 60 * 60);

    if (hoursSinceSave > STORAGE_EXPIRY_HOURS) {
      logger.debug('Onboarding', 'Saved progress expired, removing from localStorage', { hoursSinceSave, expiryHours: STORAGE_EXPIRY_HOURS });
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      return null;
    }

    logger.debug('Onboarding', 'Loaded progress from localStorage', { step: parsed.step, hoursSinceSave });
    return parsed;
  } catch (e) {
    logger.error('Onboarding', 'Failed to load onboarding progress', e);
    return null;
  }
};

const clearProgress = () => {
  localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  logger.debug('Onboarding', 'Progress cleared from localStorage');
};

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const { needsOnboarding, isLoading: authLoading } = useAuth();
  // Update the useState initializers
  const [formData, setFormData] = useState<OnboardingFormData>(() => {
    const saved = loadProgress();
    return {
      name: saved?.formData?.name || '',
      gender: saved?.formData?.gender,
      birth_date: saved?.formData?.birth_date ? new Date(saved.formData.birth_date) : null,
      birth_hour: saved?.formData?.birth_hour || '',
      birth_minute: saved?.formData?.birth_minute || '',
      birth_ampm: saved?.formData?.birth_ampm || '',
      birth_city: saved?.formData?.birth_city || null,
      birth_timezone: saved?.formData?.birth_timezone,
      astrology_style: saved?.formData?.astrology_style || 'western'
    };
  });

  const [step, setStep] = useState(() => {
    const saved = loadProgress();
    return saved?.step || 1;
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [needsTimezone, setNeedsTimezone] = useState(false); // Reserved for future timezone selector feature
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    logger.componentMount('Onboarding', { step, needsOnboarding, authLoading });
    return () => {
      logger.componentUnmount('Onboarding');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount, values are for logging only

  useEffect(() => {
    logger.stateChange('Onboarding', 'step', null, step);
  }, [step]);

  useEffect(() => {
    logger.stateChange('Onboarding', 'isLoading', false, isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (error) {
      logger.error('Onboarding', 'Error state set', null, { error });
    }
  }, [error]);

  // Add useEffect to save progress on changes
  useEffect(() => {
    if (!authLoading && needsOnboarding) {
      saveProgress(formData, step);
    }
  }, [formData, step, authLoading, needsOnboarding]);

  // Redirect if onboarding is already complete
  // Only redirect on initial load, not during the submission process
  useEffect(() => {
    if (!authLoading && !needsOnboarding && !isLoading) {
      logger.info('Onboarding', 'Onboarding already complete, redirecting to user page');
      // User has already completed onboarding, redirect to user page
      navigate('/user', { replace: true });
    }
  }, [needsOnboarding, authLoading, navigate, isLoading]);

  const [citySearchLoading, setCitySearchLoading] = useState(false);

  // GeoNames city search function for AsyncSelect
  const loadCityOptions = async (inputValue: string): Promise<Array<{ value: CityData; label: string }>> => {
    if (!inputValue || inputValue.length < 2) {
      logger.debug('Onboarding', 'City search skipped - input too short', { inputLength: inputValue?.length || 0 });
      return [];
    }

    logger.userAction('Onboarding', 'searchCities', { query: inputValue });
    setCitySearchLoading(true);
    try {
      const cities = await searchCities(inputValue, 20);
      logger.info('Onboarding', 'City search completed', { query: inputValue, resultCount: cities.length });
      return cities.map(city => ({
        value: city,
        label: `${city.name}, ${city.country}${city.region ? ` (${city.region})` : ''}`
      }));
    } catch (error) {
      logger.error('Onboarding', 'City search failed', error, { query: inputValue });
      setError('Failed to search cities. Please try again.');
      return [];
    } finally {
      setCitySearchLoading(false);
    }
  };

  // Geolocation removed - users should manually enter their birth location
  // This is more accurate and respects privacy

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    logger.debug('Onboarding', 'Input changed', { name, value, type });

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => {
        logger.stateChange('Onboarding', `formData.${name}`, prev[name as keyof OnboardingFormData], checked);
        return { ...prev, [name]: checked };
      });
    } else {
      setFormData(prev => {
        logger.stateChange('Onboarding', `formData.${name}`, prev[name as keyof OnboardingFormData], value);
        return { ...prev, [name]: value };
      });
    }
  };


  const handleNext = () => {
    logger.userAction('Onboarding', 'handleNext', { currentStep: step });
    // Basic validation
    if (step === 1 && !formData.name.trim()) {
      logger.warn('Onboarding', 'Validation failed - name required', { step });
      setError('Please enter your name');
      return;
    }
    if (step === 2 && !formData.birth_date) {
      logger.warn('Onboarding', 'Validation failed - birth date required', { step });
      setError('Please select your birth date');
      return;
    }
    if (step === 2 && !formData.birth_city) {
      logger.warn('Onboarding', 'Validation failed - birth city required', { step });
      setError('Please select your birth city');
      return;
    }
    if (step === 2 && needsTimezone && !formData.birth_timezone) {
      logger.warn('Onboarding', 'Validation failed - timezone required', { step, needsTimezone });
      setError('Please select your birth timezone');
      return;
    }
    setError('');
    logger.info('Onboarding', 'Moving to next step', { fromStep: step, toStep: step + 1 });
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    logger.userAction('Onboarding', 'handleBack', { currentStep: step });
    setError('');
    logger.info('Onboarding', 'Moving to previous step', { fromStep: step, toStep: step - 1 });
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    logger.userAction('Onboarding', 'handleSubmit', { step, formData: { hasName: !!formData.name, hasBirthDate: !!formData.birth_date, hasCity: !!formData.birth_city } });
    try {
      setIsLoading(true);
      setError('');

      // Validate required fields
      if (!formData.birth_date) {
        logger.warn('Onboarding', 'Submit validation failed - birth date required');
        setError('Please select your birth date');
        setIsLoading(false);
        return;
      }

      if (!formData.birth_city) {
        logger.warn('Onboarding', 'Submit validation failed - birth city required');
        setError('Please select your birth city');
        setIsLoading(false);
        return;
      }

      if (!formData.birth_city.name || !formData.birth_city.country) {
        logger.warn('Onboarding', 'Submit validation failed - invalid birth city');
        setError('Please select a valid birth city');
        setIsLoading(false);
        return;
      }

      // Combine time components into HH:MM format
      let birth_time = null;
      if (formData.birth_hour && formData.birth_minute && formData.birth_ampm) {
        let hour = parseInt(formData.birth_hour);
        if (formData.birth_ampm === 'PM' && hour !== 12) {
          hour += 12;
        } else if (formData.birth_ampm === 'AM' && hour === 12) {
          hour = 0;
        }
        birth_time = `${hour.toString().padStart(2, '0')}:${formData.birth_minute}`;
      }

      // Prepare data for backend
      const onboardingData = {
        name: formData.name.trim() || undefined,
        gender: formData.gender,
        tracks_period: false,
        birth_date: formData.birth_date.toISOString().split('T')[0],
        birth_time: birth_time,
        birth_city: formData.birth_city.name,
        birth_country: formData.birth_city.country,
        birth_timezone: formData.birth_timezone,
        birth_latitude: formData.birth_city.lat || 0,
        birth_longitude: formData.birth_city.lng || 0,
        astrology_style: formData.astrology_style
      };

      logger.info('Onboarding', 'Submitting onboarding data', { onboardingData: { ...onboardingData, birth_city: formData.birth_city.name } });

      // Step 1: Complete onboarding (user profile)
      logger.apiCall('Onboarding', 'POST', '/user/onboarding', onboardingData);
      const onboardingResponse = await retryWithBackoff(() => api.post('/user/onboarding', onboardingData));
      logger.apiResponse('Onboarding', 'POST', '/user/onboarding', 200, onboardingResponse.data);

      // Step 2: Prepare chart data and navigate to calculating page
      const chartData = {
        birth_date: onboardingData.birth_date,
        birth_time: birth_time,
        birth_city: onboardingData.birth_city,
        birth_country: onboardingData.birth_country,
        birth_timezone: formData.birth_timezone,
        birth_latitude: formData.birth_city?.lat,
        birth_longitude: formData.birth_city?.lng
      };

      // Clear saved progress and navigate to calculating chart page
      clearProgress();
      logger.info('Onboarding', 'Onboarding data submitted, navigating to calculating chart');
      navigate('/calculating-chart', { state: chartData, replace: true });
    } catch (err: any) {
      const errorMessage = getErrorMessage(err);
      logger.error('Onboarding', 'Onboarding/Chart calculation error', err, {
        errorMessage,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: err.config?.url
      });
      setError(`Failed to complete onboarding: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Use keyboard navigation in each step
  useKeyboardNavigation(handleNext, step > 1 ? handleBack : undefined);

  // Show loading while checking auth status
  if (authLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#050508' }}>
        <Background />
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cosmic-gold mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if onboarding is already complete (redirect will happen)
  if (!needsOnboarding) {
    return null;
  }

  // Pastel colors for progress indicators (matching landing page planet colors)
  const progressColors = ['#FFE5A0', '#E8E4F0', '#B8E6F5']; // Sun, Moon, Mercury pastels

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 sm:px-6 py-8" style={{ backgroundColor: '#050508' }}>
      <Background />

      <div className="max-w-xl w-full relative z-10 animate-fade-in">
        {/* Progress Indicator - Minimal & Luxe */}
        <div className="flex items-center justify-center mb-12 space-x-4">
          {[1, 2, 3].map((s) => {
            const isActive = s === step;
            const isCompleted = s < step;

            return (
              <div
                key={s}
                className={`
                  h-1 rounded-full transition-all duration-700 ease-out
                  ${isActive ? 'w-12 bg-gradient-to-r from-cosmic-gold to-amber-300 shadow-[0_0_15px_rgba(255,184,79,0.5)]' :
                    isCompleted ? 'w-4 bg-cosmic-gold/40' : 'w-2 bg-white/10'}
                `}
              />
            );
          })}
        </div>

        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-b from-cosmic-gold/5 to-transparent rounded-3xl blur-3xl -z-10" />

          <div className="glass-grain rounded-3xl p-8 sm:p-10 backdrop-blur-2xl border border-white/10 shadow-2xl" style={{
            backgroundColor: 'rgba(20, 20, 25, 0.4)',
            boxShadow: '0 20px 80px -20px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(255,255,255,0.05)'
          }}>

            {error && (
              <div className="mb-8 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-200 text-sm font-elegant tracking-wide animate-fade-in flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}

            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div className="space-y-8 animate-fade-in">
                <div className="text-center space-y-2">
                  <h2 className="text-3xl sm:text-4xl font-heading font-light text-white tracking-wide">
                    Welcome
                  </h2>
                  <p className="text-text-secondary font-elegant text-lg tracking-wide opacity-60">
                    Let's begin your journey
                  </p>
                </div>

                <div className="space-y-6">
                  <Input
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="How should we address you?"
                    autoFocus
                  />

                  <CustomSelect
                    label="Gender"
                    name="gender"
                    value={formData.gender || ''}
                    onChange={handleInputChange}
                    options={[
                      { value: '', label: 'Prefer not to say' },
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                      { value: 'other', label: 'Other' }
                    ]}
                    placeholder="Select gender"
                  />
                </div>

                <div className="pt-4">
                  <Button
                    onClick={handleNext}
                    className="w-full py-4 text-lg group/btn relative overflow-hidden"
                    variant="secondary"
                  >
                    <span className="relative z-10 flex items-center justify-center gap-3">
                      Continue
                      <svg
                        className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </span>
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Birth Information */}
            {step === 2 && (
              <div className="space-y-8 animate-fade-in">
                <div className="text-center space-y-2">
                  <h2 className="text-3xl sm:text-4xl font-heading font-light text-white tracking-wide">
                    Birth Details
                  </h2>
                  <p className="text-text-secondary font-elegant text-lg tracking-wide opacity-60">
                    To map your cosmic blueprint
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="w-full">
                    <label className="block text-sm font-medium text-text-secondary mb-2 font-elegant tracking-wide uppercase text-xs opacity-80">
                      Birth Date *
                    </label>
                    <DatePicker
                      selected={formData.birth_date}
                      onChange={(date) => {
                        logger.userAction('Onboarding', 'birthDateSelected', { date: date?.toISOString() });
                        setFormData(prev => ({ ...prev, birth_date: date }));
                      }}
                      dateFormat="MMMM d, yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="scroll"
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      maxDate={new Date()}
                      className="w-full"
                      wrapperClassName="w-full"
                      popperClassName="react-datepicker-popper-custom"
                      withPortal={isMobile}
                      popperPlacement="bottom-start"
                      placeholderText="Select date"
                    />
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-text-secondary mb-2 font-elegant tracking-wide uppercase text-xs opacity-80">
                      Birth Time <span className="opacity-50 lowercase font-normal ml-1">(optional)</span>
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <CustomSelect
                        name="birth_hour"
                        value={formData.birth_hour}
                        onChange={handleInputChange}
                        options={[
                          { value: '', label: 'Hour' },
                          ...Array.from({ length: 12 }, (_, i) => ({
                            value: (i + 1).toString(),
                            label: (i + 1).toString()
                          }))
                        ]}
                        placeholder="Hour"
                      />
                      <CustomSelect
                        name="birth_minute"
                        value={formData.birth_minute}
                        onChange={handleInputChange}
                        options={[
                          { value: '', label: 'Min' },
                          ...Array.from({ length: 60 }, (_, i) => ({
                            value: i.toString().padStart(2, '0'),
                            label: i.toString().padStart(2, '0')
                          }))
                        ]}
                        placeholder="Min"
                      />
                      <CustomSelect
                        name="birth_ampm"
                        value={formData.birth_ampm}
                        onChange={handleInputChange}
                        options={[
                          { value: '', label: 'AM/PM' },
                          { value: 'AM', label: 'AM' },
                          { value: 'PM', label: 'PM' }
                        ]}
                        placeholder="AM/PM"
                      />
                    </div>
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-text-secondary mb-2 font-elegant tracking-wide uppercase text-xs opacity-80">
                      Birth City *
                    </label>
                    <AsyncSelect
                      value={formData.birth_city ? {
                        value: formData.birth_city,
                        label: `${formData.birth_city.name}, ${formData.birth_city.country}${formData.birth_city.region ? ` (${formData.birth_city.region})` : ''}`
                      } : null}
                      onChange={(selected) => {
                        logger.userAction('Onboarding', 'birthCitySelected', { city: selected?.value?.name, country: selected?.value?.country });
                        setFormData(prev => ({ ...prev, birth_city: selected?.value || null }));
                      }}
                      loadOptions={loadCityOptions}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="Search city..."
                      isSearchable={true}
                      isLoading={citySearchLoading}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue.length < 2
                          ? 'Type 2+ characters...'
                          : 'No cities found'
                      }
                      loadingMessage={() => 'Searching...'}
                      menuPortalTarget={document.body}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderColor: state.isFocused ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '1rem',
                          padding: '0.35rem',
                          color: '#F5F5F5',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                          },
                          fontSize: '1rem',
                          fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                          boxShadow: state.isFocused
                            ? '0 0 20px rgba(255, 255, 255, 0.1)'
                            : 'none',
                          backdropFilter: 'blur(16px)',
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#F5F5F5',
                          fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                          letterSpacing: '0.02em',
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: 'rgba(255, 255, 255, 0.3)',
                          fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                        }),
                        menu: (provided) => ({
                          ...provided,
                          backgroundColor: 'rgba(20, 20, 25, 0.95)',
                          backdropFilter: 'blur(24px) saturate(180%)',
                          borderRadius: '1rem',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 20px 40px -10px rgba(0,0,0,0.5)',
                          padding: '0.5rem',
                        }),
                        menuPortal: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? 'rgba(255, 255, 255, 0.1)'
                            : state.isFocused
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'transparent',
                          color: state.isSelected ? '#fff' : 'rgba(255, 255, 255, 0.8)',
                          fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                          transition: 'all 0.2s ease',
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          padding: '10px 12px',
                          margin: '2px 0',
                        }),
                        input: (provided) => ({
                          ...provided,
                          color: '#F5F5F5',
                          fontFamily: "'Cormorant Garamond', 'Cormorant', serif",
                        }),
                      }}
                    />
                  </div>

                  {needsTimezone && (
                    <div className="w-full animate-fade-in">
                      <CustomSelect
                        label="Birth Timezone *"
                        name="birth_timezone"
                        value={formData.birth_timezone || ''}
                        onChange={handleInputChange}
                        options={COMMON_TIMEZONES.map(tz => ({
                          value: tz.value,
                          label: `${tz.label} (${tz.utcOffset})`
                        }))}
                        placeholder="Select timezone"
                      />
                      <p className="mt-2 text-xs text-text-tertiary font-elegant tracking-wide opacity-60">
                        Please select manually
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-between gap-4 pt-4">
                  <Button
                    variant="ghost"
                    onClick={handleBack}
                    className="min-w-[100px] text-text-secondary hover:text-white transition-colors"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleNext}
                    className="min-w-[140px] group/btn"
                    variant="secondary"
                  >
                    <span className="flex items-center justify-center gap-2">
                      Next
                      <svg
                        className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </span>
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Astrology Preference */}
            {step === 3 && (
              <div className="space-y-8 animate-fade-in">
                <div className="text-center space-y-2">
                  <h2 className="text-3xl sm:text-4xl font-heading font-light text-white tracking-wide">
                    Your Style
                  </h2>
                  <p className="text-text-secondary font-elegant text-lg tracking-wide opacity-60">
                    Choose your astrological system
                  </p>
                </div>

                <div className="space-y-4">
                  <label
                    className="flex items-start gap-4 p-6 rounded-2xl cursor-pointer transition-all duration-300 group relative overflow-hidden"
                    style={{
                      backgroundColor: formData.astrology_style === 'western'
                        ? 'rgba(255, 184, 79, 0.1)'
                        : 'rgba(255, 255, 255, 0.03)',
                      border: formData.astrology_style === 'western'
                        ? '1px solid rgba(255, 184, 79, 0.4)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <div className="mt-1">
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${formData.astrology_style === 'western' ? 'border-cosmic-gold bg-cosmic-gold/20' : 'border-white/30'
                        }`}>
                        {formData.astrology_style === 'western' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-cosmic-gold" />
                        )}
                      </div>
                      <input
                        type="radio"
                        name="astrology_style"
                        value="western"
                        checked={formData.astrology_style === 'western'}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                    </div>
                    <div className="flex-1">
                      <span className={`block text-lg font-heading mb-1 transition-colors ${formData.astrology_style === 'western' ? 'text-cosmic-gold' : 'text-white'
                        }`}>
                        Western Astrology
                      </span>
                      <span className="text-sm text-text-secondary font-elegant leading-relaxed opacity-80">
                        The most common system in the West, based on the tropical zodiac and seasons. Focuses on psychological insights and personality.
                      </span>
                    </div>
                  </label>

                  <label
                    className="flex items-start gap-4 p-6 rounded-2xl cursor-pointer transition-all duration-300 group relative overflow-hidden"
                    style={{
                      backgroundColor: formData.astrology_style === 'vedic'
                        ? 'rgba(255, 184, 79, 0.1)'
                        : 'rgba(255, 255, 255, 0.03)',
                      border: formData.astrology_style === 'vedic'
                        ? '1px solid rgba(255, 184, 79, 0.4)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <div className="mt-1">
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${formData.astrology_style === 'vedic' ? 'border-cosmic-gold bg-cosmic-gold/20' : 'border-white/30'
                        }`}>
                        {formData.astrology_style === 'vedic' && (
                          <div className="w-2.5 h-2.5 rounded-full bg-cosmic-gold" />
                        )}
                      </div>
                      <input
                        type="radio"
                        name="astrology_style"
                        value="vedic"
                        checked={formData.astrology_style === 'vedic'}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                    </div>
                    <div className="flex-1">
                      <span className={`block text-lg font-heading mb-1 transition-colors ${formData.astrology_style === 'vedic' ? 'text-cosmic-gold' : 'text-white'
                        }`}>
                        Vedic Astrology (Jyotish)
                      </span>
                      <span className="text-sm text-text-secondary font-elegant leading-relaxed opacity-80">
                        Ancient Indian system based on the sidereal zodiac and fixed stars. Focuses on destiny, karma, and predictive accuracy.
                      </span>
                    </div>
                  </label>
                </div>

                <div className="flex justify-between gap-4 pt-4">
                  <Button
                    variant="ghost"
                    onClick={handleBack}
                    className="min-w-[100px] text-text-secondary hover:text-white transition-colors"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="min-w-[140px] group/btn"
                    variant="primary"
                  >
                    <span className="flex items-center justify-center gap-2">
                      {isLoading ? 'Calculating...' : 'Reveal Chart'}
                      {!isLoading && (
                        <svg
                          className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform duration-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;

